import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { useAuthStore } from "../../store/auth";
import { UserInfo } from "../../contexts/UserContext";
import { loginRedirectURL } from "../../hooks/useLogout";

type AppRole = "SUPER_ADMIN" | "STAFF_USER" | "BASIC_USER";

/** Keys from the useAuth hook that represent individual permissions. */
type PermissionKey =
  | "canCreate"
  | "canUpdate"
  | "canDelete"
  | "canManageRestrictions"
  | "canManageUsers"
  | "canManageConfiguration"
  | "canBulkApiDataImport";

interface IProtectedRouteProps {
  children: ReactNode;
  /** Legacy role-based guard — kept for backwards compatibility. */
  allowedRoles?: AppRole[];
  /**
   * Permission-based guard. The user must have at least one of the listed
   * permissions (OR logic). Checked after authentication and allowedRoles.
   */
  requiredPermissions?: PermissionKey[];
  /**
   * Superuser-only guard for platform-level surfaces (create/list all orgs).
   * Non-superusers are bounced to the console home rather than /unauthorized,
   * since org admins legitimately reach the console — just not these routes.
   */
  requireSuperuser?: boolean;
  /**
   * Lets staff and superusers through a `requiredPermissions` check.
   *
   * Needed on the Admin Console shell: the shell demands a management
   * permission, but Developer Tools inside it is staff-gated and holds none of
   * them. Without this the shell rejects a staff user before their own route
   * guard is ever reached.
   */
  allowStaff?: boolean;
}

const getUserAppRole = (user: UserInfo): AppRole => {
  if (user?.is_superuser) return "SUPER_ADMIN";
  if (user?.is_staff) return "STAFF_USER";
  return "BASIC_USER";
};

export const ProtectedRoute = ({ children, allowedRoles, requiredPermissions, requireSuperuser, allowStaff }: IProtectedRouteProps) => {
  const {
    isAuthenticated,
    canCreate,
    canUpdate,
    canDelete,
    canManageRestrictions,
    canManageUsers,
    canManageConfiguration,
    canBulkApiDataImport
  } = useAuth();
  const { user, isAuthCheckComplete } = useAuthStore((state) => state);
  const location = useLocation();

  if (!isAuthenticated) {
    const redirectUrl = loginRedirectURL();
    return <Navigate to={redirectUrl} replace state={{ from: location }} />;
  }

  // Token exists but user bootstrap is still in-flight: avoid premature redirects.
  if (!isAuthCheckComplete) {
    return null;
  }

  if (!user) {
    const redirectUrl = loginRedirectURL();
    return <Navigate to={redirectUrl} replace state={{ from: location }} />;
  }

  if (requireSuperuser && !user.is_superuser) {
    return <Navigate to='/admin' replace />;
  }

  if (allowedRoles) {
    const currentUserRole = getUserAppRole(user as UserInfo);
    if (!allowedRoles.includes(currentUserRole)) {
      return <Navigate to='/unauthorized' replace />;
    }
  }

  const staffBypass = allowStaff && (user.is_staff || user.is_superuser);

  if (requiredPermissions && !staffBypass) {
    const permMap: Record<PermissionKey, boolean> = {
      canCreate,
      canUpdate,
      canDelete,
      canManageRestrictions,
      canManageUsers,
      canManageConfiguration,
      canBulkApiDataImport
    };
    const hasPermission = requiredPermissions.some((p) => permMap[p]);
    if (!hasPermission) {
      return <Navigate to='/unauthorized' replace />;
    }
  }

  return children;
};
